import * as React from 'react'
import { createFileRoute } from '@tanstack/react-router'
import type { SortingState, RowSelectionState, VisibilityState } from '@tanstack/react-table'
import { useReactTable, getCoreRowModel } from '@tanstack/react-table'

import { Header } from '@/components/layout/header'
import { DataTable } from '@/components/ui/data-table'
import { RedemptionSummaryCards } from '@/components/redemption/redemption-summary-cards'
import { RedemptionToolbar, type RiskFilter } from '@/components/redemption/redemption-toolbar'
import { getRedemptionColumns } from '@/components/redemption/redemption-columns'
import { RedemptionSheet } from '@/components/redemption/redemption-sheet'
import { useRedemption } from '@/hooks/useRedemption'
import { useBranch, type BranchId } from '@/providers/branch-provider'
import type { RedemptionPatientRow } from '@/lib/api-client'

export const Route = createFileRoute('/redemption')({
  component: RedemptionPage,
})

const BRANCH_IDS: Record<BranchId, number | undefined> = {
  all: undefined,
  'shah-alam': 1,
  putrajaya: 2,
  bangi: 3,
  'kuala-lumpur': 4,
}

function RedemptionPage() {
  const { branchId, selectedBranch } = useBranch()

  const [page, setPage] = React.useState(1)
  const [pageSize] = React.useState(20)
  const [sorting, setSorting] = React.useState<SortingState>([{ id: 'outstanding', desc: true }])
  const [rowSelection, setRowSelection] = React.useState<RowSelectionState>({})
  const [columnVisibility, setColumnVisibility] = React.useState<VisibilityState>({})

  const [search, setSearch] = React.useState('')
  const [debouncedSearch, setDebouncedSearch] = React.useState('')
  const [riskTier, setRiskTier] = React.useState<RiskFilter>('all')
  const [minOutstanding, setMinOutstanding] = React.useState(1)

  // Sheet state
  const [selectedPatientId, setSelectedPatientId] = React.useState<number | null>(null)
  const [sheetOpen, setSheetOpen] = React.useState(false)

  // Debounce search
  React.useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(search)
      setPage(1)
    }, 300)
    return () => clearTimeout(t)
  }, [search])

  // Reset page when filters change
  React.useEffect(() => {
    setPage(1)
  }, [branchId, riskTier, minOutstanding])

  const sortBy = (sorting[0]?.id as 'outstanding' | 'lastVisit' | 'name' | undefined) ?? 'outstanding'
  const sortOrder = sorting[0]?.desc ? 'desc' : 'asc'

  const { data, isPending, isError } = useRedemption({
    branchId: BRANCH_IDS[branchId],
    riskTier,
    search: debouncedSearch || undefined,
    minOutstanding,
    page,
    pageSize,
    sortBy,
    sortOrder,
  })

  const handleView = React.useCallback((row: RedemptionPatientRow) => {
    setSelectedPatientId(row.patientId)
    setSheetOpen(true)
  }, [])

  const columns = React.useMemo(() => getRedemptionColumns({ onView: handleView }), [handleView])

  const pageCount = data ? Math.max(1, Math.ceil(data.pagination.total / pageSize)) : -1

  const table = useReactTable({
    data: data?.patients ?? [],
    columns,
    pageCount,
    state: {
      sorting,
      rowSelection,
      columnVisibility,
      pagination: { pageIndex: page - 1, pageSize },
    },
    onSortingChange: setSorting,
    onRowSelectionChange: setRowSelection,
    onColumnVisibilityChange: setColumnVisibility,
    onPaginationChange: (updater) => {
      const next = typeof updater === 'function' ? updater({ pageIndex: page - 1, pageSize }) : updater
      setPage(next.pageIndex + 1)
    },
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    manualSorting: true,
  })

  const subtitle = data
    ? `${data.summary.totalOutstanding.toLocaleString()} patients with outstanding sessions · ${selectedBranch.name}`
    : 'Loading...'

  return (
    <div className="flex flex-col h-full">
      <Header title="Redemption Tracker" subtitle={subtitle} />

      <div className="flex-1 p-3 sm:p-4 lg:p-6 space-y-4">
        <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-sm text-amber-600 dark:text-amber-400">
          <span className="font-semibold">⚠️ Deprecated — inaccurate estimate.</span>{' '}
          These figures are reconstructed from invoice lines and are wrong at the per-patient
          level (package contents and redemption progress live only in Avixo). Do not use this
          for call lists. The accurate data lands in the Packages report — this page will be
          replaced once it is validated.
        </div>

        <RedemptionSummaryCards
          data={data?.summary}
          isLoading={isPending}
          activeFilter={riskTier}
          onFilterChange={setRiskTier}
        />

        <RedemptionToolbar
          search={search}
          onSearchChange={setSearch}
          riskTier={riskTier}
          onRiskTierChange={setRiskTier}
          minOutstanding={minOutstanding}
          onMinOutstandingChange={setMinOutstanding}
        />

        {isError && (
          <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive">
            Failed to load redemption data. Check your connection and refresh.
          </div>
        )}

        <DataTable
          columns={columns}
          data={data?.patients ?? []}
          isLoading={isPending}
          pageCount={pageCount}
          pageIndex={page - 1}
          pageSize={pageSize}
          onPaginationChange={({ pageIndex }) => setPage(pageIndex + 1)}
          sorting={sorting}
          onSortingChange={setSorting}
          rowSelection={rowSelection}
          onRowSelectionChange={setRowSelection}
          onRowClick={handleView}
          emptyMessage="No patients with outstanding sessions match these filters."
        />
      </div>

      <RedemptionSheet
        patientId={selectedPatientId}
        open={sheetOpen}
        onOpenChange={setSheetOpen}
      />
    </div>
  )
}
